import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { getProjectScope } from '@/lib/projectScope';
import { DAILY_LOG_SYNC_EVENT } from '@/lib/dailyLogUtils';

const REMOTE_REFRESH_INTERVAL_MS = 60_000;
const REMOTE_REFRESH_DEBOUNCE_MS = 400;

export interface DailyLogReviewPhoto {
  id: string;
  project_id: string;
  file_name: string;
  file_url: string;
  file_type: string | null;
  file_size: string | null;
  log_date: string | null;
  caption: string | null;
  note: string | null;
  review_status: string | null;
  is_client_visible: boolean;
  is_visible: boolean;
  is_deleted: boolean;
  uploaded_by: string | null;
  created_at: string;
  stage_id: string | null;
  stage_name: string | null;
  daily_log_id?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

export interface ProjectFilterItem {
  id: string;
  project_name: string;
  project_code: string;
}

export interface StageFilterItem {
  id: string;
  stage_name: string;
  project_id: string;
}

export interface ProfileItem {
  id: string;
  full_name: string | null;
  company_name: string | null;
}

export interface DailyLogEntry {
  id: string;
  project_id: string;
  log_date: string;
  title: string | null;
  management_comment: string | null;
  internal_note: string | null;
  visibility: string;
  is_published_to_client: boolean;
  published_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
  is_deleted: boolean;
}

const IMAGE_TYPES = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'gif', 'bmp', 'tiff', 'avif', 'image']);
function isImage(row: DailyLogReviewPhoto): boolean {
  const type = String(row.file_type ?? '').toLowerCase();
  const ext = String(row.file_name ?? '').split('.').pop()?.toLowerCase() ?? '';
  return IMAGE_TYPES.has(type) || IMAGE_TYPES.has(ext) || (!type && Boolean(ext));
}

export function useManagementDailyLog() {
  const [photos, setPhotos] = useState<DailyLogReviewPhoto[]>([]);
  const [projects, setProjects] = useState<ProjectFilterItem[]>([]);
  const [stages, setStages] = useState<StageFilterItem[]>([]);
  const [profiles, setProfiles] = useState<ProfileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadKey, setLoadKey] = useState(0);
  const [dailyLogs, setDailyLogs] = useState<DailyLogEntry[]>([]);
  const [dailyLogsLoading, setDailyLogsLoading] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadData = useCallback(async () => {
    const firstLoad = !hasLoadedRef.current;
    if (firstLoad) setLoading(true);
    setDailyLogsLoading(true);
    setError('');
    try {
      const scope = await getProjectScope();
      if (!scope.isFullAccess && (!scope.visibleProjectIds || scope.visibleProjectIds.length === 0)) {
        setPhotos([]);
        setProjects([]);
        setStages([]);
        setProfiles([]);
        setDailyLogs([]);
        setLastSyncedAt(new Date().toISOString());
        hasLoadedRef.current = true;
        return;
      }

      const visibleIds = scope.visibleProjectIds;
      let photoQuery = supabase
        .from('jphph_project_files')
        .select('id, project_id, file_name, file_url, file_type, file_size, log_date, caption, stage_id, review_status, is_client_visible, is_visible, is_deleted, note, created_at, uploaded_by, daily_log_id, storage_bucket, storage_path')
        .eq('is_deleted', false)
        .or('uploaded_context.eq.daily_log,uploaded_context.is.null')
        .order('log_date', { ascending: false })
        .order('created_at', { ascending: false });
      if (visibleIds) photoQuery = photoQuery.in('project_id', visibleIds);

      let projQuery = supabase
        .from('jphph_projects')
        .select('id, project_name, project_code')
        .eq('is_archived', false)
        .order('created_at', { ascending: false })
        .limit(100);
      if (visibleIds) projQuery = projQuery.in('id', visibleIds);

      let stageQuery = supabase
        .from('jphph_project_stages')
        .select('id, project_id, stage_name')
        .order('sort_order', { ascending: true });
      if (visibleIds) stageQuery = stageQuery.in('project_id', visibleIds);

      let logQuery = supabase
        .from('jphph_daily_logs')
        .select('id, project_id, log_date, title, management_comment, internal_note, visibility, is_published_to_client, published_at, created_by, created_at, updated_at, is_deleted')
        .eq('is_deleted', false)
        .order('log_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(500);
      if (visibleIds) logQuery = logQuery.in('project_id', visibleIds);

      const [photoResult, projectResult, stageResult, profileResult, logResult] = await Promise.all([
        photoQuery,
        projQuery,
        stageQuery,
        supabase.from('jphph_profiles').select('id, full_name, company_name').limit(500),
        logQuery,
      ]);

      if (photoResult.error) throw photoResult.error;
      if (projectResult.error) throw projectResult.error;
      if (stageResult.error) throw stageResult.error;
      if (profileResult.error) throw profileResult.error;
      if (logResult.error) throw logResult.error;

      const records = ((photoResult.data ?? []) as DailyLogReviewPhoto[]).filter(isImage);
      const stageMap = new Map(((stageResult.data ?? []) as StageFilterItem[]).map((s) => [s.id, s.stage_name]));
      setPhotos(records.map((r) => ({ ...r, stage_name: r.stage_id ? stageMap.get(r.stage_id) || null : null })));
      setProjects((projectResult.data ?? []) as ProjectFilterItem[]);
      setStages((stageResult.data ?? []) as StageFilterItem[]);
      setProfiles((profileResult.data ?? []) as ProfileItem[]);
      setDailyLogs((logResult.data ?? []) as DailyLogEntry[]);
      setLastSyncedAt(new Date().toISOString());
      hasLoadedRef.current = true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      if (!hasLoadedRef.current) {
        setPhotos([]);
        setDailyLogs([]);
      }
    } finally {
      setDailyLogsLoading(false);
      setLoading(false);
    }
  }, [loadKey]);

  useEffect(() => { void loadData(); }, [loadData]);

  useEffect(() => {
    const handler = () => setLoadKey((k) => k + 1);
    window.addEventListener(DAILY_LOG_SYNC_EVENT, handler);
    return () => window.removeEventListener(DAILY_LOG_SYNC_EVENT, handler);
  }, []);

  useEffect(() => {
    let disposed = false;
    const scheduleRefresh = () => {
      if (disposed) return;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        if (!disposed) setLoadKey((k) => k + 1);
      }, REMOTE_REFRESH_DEBOUNCE_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') scheduleRefresh();
    };
    const channel = supabase
      .channel('jph-management-daily-log-sync')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jphph_project_files' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jphph_daily_logs' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jphph_daily_log_photos' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jphph_daily_log_files' }, scheduleRefresh)
      .subscribe();

    const interval = window.setInterval(scheduleRefresh, REMOTE_REFRESH_INTERVAL_MS);
    window.addEventListener('focus', scheduleRefresh);
    window.addEventListener('online', scheduleRefresh);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      window.clearInterval(interval);
      window.removeEventListener('focus', scheduleRefresh);
      window.removeEventListener('online', scheduleRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
      void supabase.removeChannel(channel);
    };
  }, []);

  const refresh = useCallback(() => setLoadKey((k) => k + 1), []);
  return { photos, projects, stages, profiles, dailyLogs, dailyLogsLoading, loading, error, lastSyncedAt, refresh };
}

export async function updateReviewStatus(
  photoIds: string[],
  status: 'approved_for_client' | 'internal_only' | 'rejected' | 'pending_management_review',
  managementNote?: string
) {
  const updatePayload: Record<string, unknown> = {
    review_status: status,
    is_client_visible: status === 'approved_for_client',
    updated_at: new Date().toISOString(),
  };
  if (managementNote !== undefined) updatePayload.note = managementNote;
  const { error } = await supabase.from('jphph_project_files').update(updatePayload).in('id', photoIds);
  if (error) throw error;
  window.dispatchEvent(new CustomEvent(DAILY_LOG_SYNC_EVENT));
  return true;
}

export async function deletePhoto(photoId: string) {
  const { error } = await supabase
    .from('jphph_project_files')
    .update({ is_deleted: true, is_visible: false, is_client_visible: false, updated_at: new Date().toISOString() })
    .eq('id', photoId);
  if (error) throw error;
  window.dispatchEvent(new CustomEvent(DAILY_LOG_SYNC_EVENT));
  return true;
}
